import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Modal from '../modal'
import SearchInput from '../SearchInput'

import Button from '../CustomButton';
import Typography from '../Typography';
import Item from './Item';

// Styles
import styles from './style.module.scss';

// Types
import { APIId, Course, Folder, Note } from '../../types';

// Utils
import * as urls from '../../utils/urls';
import useCsrfToken from '../../utils/hooks/csfrToken';
import useShortCutKey from '../../hooks/useShortCutKey';

interface AddToModalProps {
  type: 'add' | 'attach';
  startingNotes?: APIId[];
  showOnly?: ('notes' | 'folders' | 'courses')[];
  single?: boolean;
  title?: string;
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
  onAttach?: (folders: Folder[], notes: Note[], courses: Course[]) => void;
}

const GET_FETCH_OPTIONS = {
  method: 'GET',
  headers: { 'Accept': 'application/json' }
}

const defaultOnAttach = (folders: Folder[], notes: Note[], courses: Course[]) => {
}

const defaultOnAdded = () => {
}

const AddToModal: React.FC<AddToModalProps> = ({
  type = 'add',
  startingNotes = [],
  title,
  showOnly = ['notes', 'folders', 'courses'],
  single = false,
  open,
  onClose,
  onAdded = defaultOnAdded,
  onAttach = defaultOnAttach
}) => {
  // Hooks
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // State
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [folders, setFolders] = useState<Folder[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);

  // Selected
  const [selectedFolders, setSelectedFolders] = useState<APIId[]>([]);
  const [selectedNotes, setSelectedNotes] = useState<APIId[]>(startingNotes);
  const [selectedCourses, setSelectedCourses] = useState<APIId[]>([]);

  // Methods
  const getResources = useCallback(async (query: string, force: boolean = false) => {
    setLoading(true);
    // If the query is the same as the search query, don't fetch again
    if (query === searchQuery && !force) return setLoading(false);

    try {
      if (showOnly.includes('folders')) {
        const foldersResponse = await fetch(urls.foldersUrl(), GET_FETCH_OPTIONS)
        if (foldersResponse.ok) {
          const folders = await foldersResponse.json();
          setFolders(folders.map((data: any) => data));
        }
      }

      if (showOnly.includes('notes')) {
        const notesResponse = await fetch(urls.notesUrl(), GET_FETCH_OPTIONS)
        if (notesResponse.ok) {
          const notes = await notesResponse.json();
          const parsedNotes = notes.map((data: any) => data.data.attributes);
          setNotes(parsedNotes);
        }
      }

      if (showOnly.includes('courses')) {
        const coursesResponse = await fetch(urls.coursesUrl(), GET_FETCH_OPTIONS)
        if (coursesResponse.ok) {
          const courses = await coursesResponse.json();
          setCourses(courses);
        }
      }

      // If the query is empty, set the search query to the query
      setSearchQuery(query);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    if (open) getResources('', true);
  }, [open]);

  useEffect(() => {
    if (startingNotes) setSelectedNotes(startingNotes);
  }, [startingNotes]);

  // Handlers
  const handleSearch = useCallback((value: string) => {
    getResources(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    getResources('');
  }, []);

  const handleAdd = useCallback(async () => {
    setLoading(true);

    // TODO: Remove this
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
    if (onAdded) onAdded();

    // TODO: Keith or Dmytro or Daniel Connect to the backend
    // try {
    //   const data = new FormData();
    //   selectedFolders.forEach(folder => data.append('folders[]', folder));
    //   selectedNotes.forEach(note => data.append('notes[]', note));
    //   selectedCourses.forEach(course => data.append('courses[]', course));

    //   // Add folders
    //   const response = await fetch(urls.foldersUrl(), {
    //     method: 'POST',
    //     headers: { 'CSRF-Token': csrfToken },
    //     body: data
    //   });

    //   if (response.ok) onClose();
    // } catch (error) {
    //   console.error(error);
    // } finally {
    //   setLoading(false);
    // }
  }, [selectedFolders, selectedNotes, selectedCourses, onAdded, csrfToken]);

  const handleSelectFolder = useCallback((id: string | number) => {
    setSelectedFolders(prev => {
      if (prev.includes(id)) {
        return prev.filter(_id => _id !== id);
      }
      return [...prev, id];
    });
  }, [selectedFolders]);

  const handleSelectNote = useCallback((id: string | number) => {
    setSelectedNotes(prev => {
      if (prev.includes(id)) {
        return prev.filter(_id => _id !== id);
      }
      return [...prev, id];
    });
  }, [selectedNotes]);

  const handleSelectCourse = useCallback((id: string | number) => {
    setSelectedCourses(prev => {
      if (prev.includes(id)) {
        return prev.filter(_id => _id !== id);
      }
      return [...prev, id];
    });
  }, [selectedCourses]);

  const handleAttach = useCallback(() => {
    if (onAttach) {
      const _selectedFolders = folders.filter((folder: Folder) => selectedFolders.includes(folder.id));
      const _selectedNotes = notes.filter((note: Note) => selectedNotes.includes(note.id));
      const _selectedCourses = courses.filter((course: Course) => selectedCourses.includes(course.id));

      onAttach(_selectedFolders, _selectedNotes, _selectedCourses);
    }
  }, [selectedFolders, selectedNotes, selectedCourses, onAttach]);

  useShortCutKey({ key: 'Enter', onKeyPress: type === 'attach' ? handleAttach : handleAdd, active: open });

  const isDisabledAdd = loading || (selectedFolders.length === 0 && selectedNotes.length === 0 && selectedCourses.length === 0);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title || t('addToModal.title')}
      showCloseButton
      footer={
        <div className={styles.footer}>
          <Button
            variant="primary"
            disabled={isDisabledAdd}
            classes={{
              root: styles.add_button
            }}
            onClick={type === 'attach' ? handleAttach : handleAdd}
          >
            {type === 'attach' ? t('actions.attach') : t('actions.add')}
          </Button>
        </div>
      }
      classes={{
        header: styles.modal_header,
        content: styles.modal,
        title: styles.modal_title
      }}
    >
      <SearchInput
        value={searchQuery}
        onDebouncedSearch={handleSearch}
        placeholder={t('addToModal.search')}
      />

      <div className={styles.results}>
        {folders.length > 0 && (
          <div className={styles.folders}>
            <Typography variant="p" className={styles.label}>
              {t('addToModal.folders')}
            </Typography>

            <div className={styles.list}>
              {folders.map((folder) => (
                <Item
                  key={folder.id}
                  type="folder"
                  item={folder}
                  onSelect={handleSelectFolder}
                  selected={selectedFolders.includes(folder.id)}
                  disabled={single && !selectedFolders.includes(folder.id)}
                />
              ))}
            </div>
          </div>
        )}

        {notes.length > 0 && (
          <div className={styles.notes}>
            <Typography variant="p" className={styles.label}>
              {t('addToModal.notes')}
            </Typography>

            <div className={styles.list}>
              {notes.map((note) => (
                <Item
                  key={note.id}
                  type="note"
                  item={note}
                  onSelect={handleSelectNote}
                  selected={selectedNotes.includes(note.id)}
                  disabled={selectedNotes.length > 0 && single && !selectedNotes.includes(note.id)}
                />
              ))}
            </div>
          </div>
        )}

        {courses.length > 0 && type === 'add' && (
          <div className={styles.study_paths}>
            <Typography variant="p" className={styles.label}>
              {t('addToModal.study_paths')}
            </Typography>

            <div className={styles.list}>
              {courses.map((course) => (
                <Item
                  key={course.id}
                  type="course"
                  item={course}
                  onSelect={handleSelectCourse}
                  selected={selectedCourses.includes(course.id)}
                  disabled={single && !selectedCourses.includes(course.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

    </Modal>
  )
}

export default AddToModal;
